import { defineStore } from 'pinia'
import type { Conversation, InboxSection, ApiResponse, InboxItem, InboxFilter, Pagination as PaginationType } from '~/types'
import { InboxStatus, InboxOrder } from '~/types/inbox';
import { IconUsersGroup, IconUserScan } from '@tabler/icons-vue';

export const useConversationStore = defineStore('conversation', {
    state: () => ({
        conversations: { data: [] as Conversation[] } as PaginationType<Conversation>,
        currentConversation: null as Conversation | null,
        conversationsLoaded: false,

        inboxes: [] as InboxSection[],
        inboxesLoaded: false,
        currentFilter: {
            status: InboxStatus.OPEN,
            order: InboxOrder.NEWEST,
            team_id: null as string | null,
            user_id: null as string | null
        } as InboxFilter,
        currentInbox: null as InboxItem | null,

        layout: {
            rightbarOpen: true as boolean,
        }
    }),

    actions: {
        toggleRightbar() {
            this.layout.rightbarOpen = !this.layout.rightbarOpen
        },

        setConversations(conversations: PaginationType<Conversation>) {
            this.conversations = conversations
        },
        receiveNewConversation(conversation: Conversation) {
            this.conversations.data?.unshift(conversation);

        },
        setCurrentConversation(conversation: Conversation | null) {
            this.currentConversation = conversation
            this.layout.rightbarOpen = true
        },
        async fetchConversations(force = false, page: number = 1, filters: InboxFilter = {
                status: InboxStatus.OPEN,
                order: InboxOrder.NEWEST,
                team_id: null,
                user_id: null
            }) {
                if (this.conversationsLoaded && !force) return

                const { $api } = useNuxtApp();

                // Update current filter
                this.currentFilter = { ...this.currentFilter, ...filters };

                // Build query string from filters
                const queryParams = new URLSearchParams();
                if (filters.status) queryParams.append('status', filters.status);
                if (filters.order) queryParams.append('order', filters.order);
                if (filters.team_id) queryParams.append('team_id', filters.team_id);
                if (filters.user_id) queryParams.append('user_id', filters.user_id);
                queryParams.append('page', page.toString());
                const queryString = queryParams.toString();

                await $api.get(`/conversations?${queryString}`)
                .then((response) => {
                    // Ensure we have valid data
                    this.setConversations(response.data);
                }).catch((error) => {
                    console.error(error);
                }).finally(() => {
                    this.conversationsLoaded = true;
                })
        },
        setInboxes(inboxes: InboxSection[]) {
            this.inboxes = inboxes;
            this.inboxesLoaded = true;
        },
        async fetchInboxes(force = false) {
            if (this.inboxesLoaded && !force) return;

            const { $api } = useNuxtApp();
            const userStore = useUserStore();

            await $api.get(`/inboxes?status=${this.currentFilter.status}`).then((response) => {
                const data = response.data as ApiResponse;
                const formattedInboxes: InboxSection[] = [];

                // Add inbox section
                if (data.inbox && data.inbox.length > 0) {
                    const inboxItems = data.inbox.map((item) => {
                        if (item.name === 'assigned_to_me') {
                            return {
                                key: 'assigned-to-me',
                                name: 'Assigned to me',
                                icon: item.user?.photo || userStore.user?.photo_url,
                                iconType: 'image' as const,
                                current: this.currentFilter.user_id === item.user?.id,
                                total: item.total || 0,
                                filter: {
                                    ...this.currentFilter,
                                    user_id: item.user?.id
                                 } as InboxFilter
                            } as InboxItem;
                        } else if (item.name === 'unassigned') {
                            return {
                                key: 'unassigned',
                                name: 'Unassigned',
                                icon: IconUserScan,
                                iconType: 'icon' as const,
                                current: this.currentFilter.user_id === 'unassigned',
                                total: item.total || 0,
                                filter: {
                                    ...this.currentFilter,
                                    user_id: 'unassigned'
                                } as InboxFilter
                            } as InboxItem;
                        } else if (item.name === 'all') {
                            return {
                                key: 'all',
                                name: 'All',
                                icon: IconUsersGroup,
                                iconType: 'icon' as const,
                                current: !this.currentFilter.user_id && !this.currentFilter.team_id,
                                total: item.total || 0,
                                filter: {
                                    ...this.currentFilter,
                                    user_id: null,
                                    team_id: null
                                } as InboxFilter
                            } as InboxItem;
                        }
                        return null;
                    }).filter(Boolean) as InboxItem[];

                    formattedInboxes.push({
                        group: 'Inbox',
                        items: inboxItems
                    });
                }

                // add users section
                if (data.users && data.users.length >= 1) {
                    // Filter out the current user
                    const filteredUsers = data.users.filter(user =>
                        user.user?.id !== userStore.user?.id
                    );

                    const userItems = filteredUsers.map((user) => ({
                        key: user.user?.id || '',
                        name: user.user?.name || '',
                        icon: user.user?.photo || '',
                        iconType: 'image' as const,
                        current: this.currentFilter.user_id === user.user?.id,
                        total: user.total || 0,
                        filter: {
                            ...this.currentFilter,
                            user_id: user.user?.id
                        } as InboxFilter
                    } as InboxItem));

                    if (userItems.length > 0) {
                        formattedInboxes.push({
                            group: 'Users',
                            items: userItems
                        });
                    }
                }

                // Add teams section
                if (data.teams && data.teams.length >= 1) {
                    const teamItems = data.teams.map((team) => ({
                        key: team.team?.id || '',
                        name: team.name,
                        icon: team.team?.icon,
                        iconType: 'emoji' as const,
                        current: this.currentFilter.team_id === team.team?.id,
                        total: team.total || 0,
                        filter: {
                            ...this.currentFilter,
                            team_id: team.team?.id
                        } as InboxFilter
                    } as InboxItem));

                    formattedInboxes.push({
                        group: 'Team inboxes',
                        items: teamItems
                    });
                }

                this.setInboxes(formattedInboxes);

                // Set currentInbox based on currentFilter when loading inboxes
                if (!this.currentInbox && formattedInboxes.length > 0) {
                    // Find the item matching current filter or default to "All" item
                    let foundItem = null;

                    if (this.currentFilter.user_id) {
                        // Look for user_id match
                        for (const section of formattedInboxes) {
                            foundItem = section.items.find(item =>
                                item.filter &&
                                'user_id' in item.filter &&
                                item.filter.user_id === this.currentFilter.user_id
                            );
                            if (foundItem) break;
                        }
                    } else if (this.currentFilter.team_id) {
                        // Look for team_id match
                        for (const section of formattedInboxes) {
                            foundItem = section.items.find(item =>
                                item.filter &&
                                'team_id' in item.filter &&
                                item.filter.team_id === this.currentFilter.team_id
                            );
                            if (foundItem) break;
                        }
                    }

                    // If no match was found, look for the "All" item
                    if (!foundItem) {
                        for (const section of formattedInboxes) {
                            const allItem = section.items.find(item => item.key === 'all');
                            if (allItem) {
                                foundItem = allItem;
                                // Also reset the filter to match the All item
                                this.currentFilter.user_id = null;
                                this.currentFilter.team_id = null;
                                break;
                            }
                        }
                    }

                    // If still no item found and there are items, default to the first item
                    if (!foundItem && formattedInboxes[0].items.length > 0) {
                        foundItem = formattedInboxes[0].items[0];
                    }

                    if (foundItem) {
                        this.currentInbox = foundItem;
                    }
                }
            }).catch((error) => {
                console.error(error);
            });
        },
        setCurrentFilter(filter: InboxFilter | null) {
            if (filter) {
                // Update only the properties that are in the filter
                if ('user_id' in filter) this.currentFilter.user_id = filter.user_id ?? null;
                if ('team_id' in filter) this.currentFilter.team_id = filter.team_id ?? null;
            } else {
                // Reset filter to default
                this.currentFilter.user_id = null;
                this.currentFilter.team_id = null;
            }

            // Update which item is active
            this.inboxes.forEach(section => {
                section.items.forEach(item => {
                    const itemFilter = item.filter || { user_id: null, team_id: null };

                    if (!this.currentFilter.user_id && !this.currentFilter.team_id) {
                        // "All" case
                        item.current = item.key === 'all';
                    } else if (itemFilter.user_id && itemFilter.user_id === this.currentFilter.user_id) {
                        item.current = true;
                    } else if (itemFilter.team_id && itemFilter.team_id === this.currentFilter.team_id) {
                        item.current = true;
                    } else {
                        item.current = false;
                    }
                });
            });
        },
        setCurrentInbox(inboxItem: InboxItem | null) {
            this.currentInbox = inboxItem;
            if (inboxItem && inboxItem.filter) {
                this.setCurrentFilter(inboxItem.filter);
            } else {
                this.setCurrentFilter(null);
            }
        },
        clear() {
            this.conversations = { data: [] as Conversation[] } as PaginationType<Conversation>;
            this.currentConversation = null
            this.conversationsLoaded = false
            this.inboxes = []
            this.inboxesLoaded = false
            this.currentFilter = {
                status: 'open',
                order: 'newest',
                team_id: null,
                user_id: null
            } as InboxFilter
            this.currentInbox = null
        }
    }
})